/**
 * @fileoverview Goruntulenme Modu Hook'u
 *
 * Bu modul, ChatGPT widget goruntulenme modlarini (fullscreen/inline)
 * yoneten hook ve yardimci fonksiyonlari icerir. Ayrica "Emlakjet'te Ac"
 * butonu icin URL olusturma islevini saglar.
 *
 * @module useDisplayMode
 */

import { useCallback } from "react";
import { useOpenAiGlobal } from "../../use-openai-global";

/** @constant {string} Emlakjet temel URL'i */
const EMLAKJET_BASE_URL = "https://www.emlakjet.com";

/**
 * Mevcut goruntulemeye gore dinamik Emlakjet URL'i olusturur.
 *
 * URL, mevcut widget durumuna gore belirlenir:
 * - Detay goruntuleme: /ilan/{listingId}
 * - Arama goruntuleme: /{path}?filtreler={filters}
 * - Varsayilan: ana sayfa
 *
 * @function buildEmlakjetUrl
 * @param {string|null} selectedListingId - Secili ilan ID'si (detay goruntulemesi icin)
 * @param {string|null} currentPath - Mevcut arama yolu
 * @param {string|null} currentFilters - Mevcut filtreler
 * @returns {string} Olusturulan Emlakjet URL'i
 *
 * @example
 * // Detay goruntuleme
 * buildEmlakjetUrl("12345", null, null)
 * // Returns: "https://www.emlakjet.com/ilan/12345"
 *
 * // Arama goruntuleme
 * buildEmlakjetUrl(null, "satilik-konut/istanbul", "oda-sayisi=3-1")
 * // Returns: "https://www.emlakjet.com/satilik-konut/istanbul?filtreler=oda-sayisi%3D3-1"
 *
 * // Varsayilan
 * buildEmlakjetUrl(null, null, null)
 * // Returns: "https://www.emlakjet.com"
 */
export function buildEmlakjetUrl(selectedListingId, currentPath, currentFilters) {
  // Detail view - open specific listing
  if (selectedListingId) {
    return `${EMLAKJET_BASE_URL}/ilan/${selectedListingId}`;
  }

  // Search view - open search results
  if (currentPath) {
    const filterString = currentFilters
      ? `?filtreler=${encodeURIComponent(currentFilters)}`
      : "";
    // Ensure path starts with /
    const normalizedPath = currentPath.startsWith("/") ? currentPath : `/${currentPath}`;
    return `${EMLAKJET_BASE_URL}${normalizedPath}${filterString}`;
  }

  // Fallback to homepage
  return EMLAKJET_BASE_URL;
}

/**
 * ChatGPT basligindaki "Emlakjet'te Ac" butonu URL'ini ayarlar.
 *
 * Bu fonksiyon, `window.openai.setOpenInAppUrl` API'sini kullanarak
 * ChatGPT arayuzundeki "Uygulamada Ac" butonunun URL'ini gunceller.
 * Mevcut goruntulenme degistiginde (arama -> detay gibi) cagrilmalidir.
 *
 * @function setOpenInEmlakjetUrl
 * @param {string} url - Ayarlanacak URL
 * @returns {void}
 *
 * @example
 * // Detay sayfasi acildiginda
 * setOpenInEmlakjetUrl("https://www.emlakjet.com/ilan/12345");
 *
 * // Arama sayfasina donuldugunde
 * setOpenInEmlakjetUrl(buildEmlakjetUrl(null, currentPath, currentFilters));
 */
export function setOpenInEmlakjetUrl(url) {
  console.log("[Emlakjet] setOpenInAppUrl:", url, "API available:", !!window.openai?.setOpenInAppUrl);
  if (window.openai?.setOpenInAppUrl) {
    window.openai.setOpenInAppUrl({ href: url });
  }
}

/**
 * @typedef {Object} UseDisplayModeReturn
 * @property {string} displayMode - Mevcut goruntulenme modu ("inline" | "fullscreen" | "pip")
 * @property {boolean} isFullscreen - Tam ekran modunda mi
 * @property {Function} requestFullscreen - Tam ekran moduna gecis iste
 * @property {Function} exitFullscreen - Tam ekran modundan cikis iste
 */

/**
 * ChatGPT goruntulenme modlarini (fullscreen/inline) yonetir.
 *
 * Bu hook, ChatGPT widget'inin goruntulenme modunu kontrol eder.
 * `window.openai.requestDisplayMode` API'si ile mod degisikligi istenir.
 *
 * **Desteklenen Modlar:**
 * - `inline`: Varsayilan mod, sohbet icinde kucuk goruntulenme
 * - `fullscreen`: Tam ekran mod, harita+liste goruntulenme
 * - `pip`: Picture-in-Picture modu (destekleniyorsa)
 *
 * **Kullanim Senaryolari:**
 * - Harita goruntulenme icin fullscreen gerekir
 * - Detay modali fullscreen'de gosterilir
 * - Liste goruntulenme inline'da calisir
 *
 * @function useDisplayMode
 * @returns {UseDisplayModeReturn} Goruntulenme modu state ve fonksiyonlari
 *
 * @example
 * const { isFullscreen, requestFullscreen, exitFullscreen } = useDisplayMode();
 *
 * // Harita butonuna tiklandiginda
 * const handleMapClick = async () => {
 *   await requestFullscreen();
 *   setShowMap(true);
 * };
 *
 * // Kapatma butonuna tiklandiginda
 * const handleClose = async () => {
 *   await exitFullscreen();
 * };
 */
export function useDisplayMode() {
  const displayMode = useOpenAiGlobal("displayMode");
  const isFullscreen = displayMode === "fullscreen";

  const requestFullscreen = useCallback(async () => {
    if (window.openai?.requestDisplayMode) {
      try {
        await window.openai.requestDisplayMode({ mode: "fullscreen" });
      } catch (e) {
        console.error("Failed to request fullscreen:", e);
      }
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (window.openai?.requestDisplayMode) {
      try {
        await window.openai.requestDisplayMode({ mode: "inline" });
      } catch (e) {
        console.error("Failed to exit fullscreen:", e);
      }
    }
  }, []);

  return {
    displayMode,
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
  };
}

export default useDisplayMode;
